import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import EnhancedPlayerProfileForm from './EnhancedPlayerProfileForm';
import CoachProfileForm from './CoachProfileForm';
import TeamOwnerProfileForm from './TeamOwnerProfileForm';
import VenueProfileForm from './VenueProfileForm';
import CreativeProfileForm from './CreativeProfileForm';
import SupportProfileForm from './SupportProfileForm';
import FanProfileForm from './FanProfileForm';
import ParentProfileForm from './ParentProfileForm';
import AdminControlPanel from '../admin/AdminControlPanel';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { validateProfileCompletion, getFieldLabel } from '@/services/profileValidationService';

const RoleBasedProfileForm: React.FC = () => {
  const { profile, loading: authLoading } = useAuth();
  const { user: internalUser, profile: internalProfile, loading: internalLoading } = useInternalAuth();
  
  const isLoading = authLoading || internalLoading;
  
  // Determine user role - prioritize internal user role if available
  const userRole = internalUser?.role || profile?.role || 'player';
  const user = internalUser || profile;
  
  // Create merged profile for validation - combines internalUser (age_group, role) with internalProfile (profile fields)
  const validationProfile = React.useMemo(() => {
    if (internalUser) {
      // For internal users, merge internalUser fields with internalProfile fields
      return {
        ...internalProfile,
        // Ensure age_group comes from internalUser (where it's stored for internal users)
        age_group: internalUser.age_group || internalProfile?.age_group || '',
        role: internalUser.role || internalProfile?.role || 'player',
        // Profile fields from internalProfile
        first_name: internalProfile?.first_name || '',
        last_name: internalProfile?.last_name || '',
        current_sport: internalProfile?.current_sport || '',
        position: internalProfile?.position || '',
        skill_level: internalProfile?.skill_level || '',
        bio: internalProfile?.bio || '',
        profile_photo_url: internalProfile?.profile_photo_url || '',
        height: internalProfile?.height || '',
        weight: internalProfile?.weight || '',
        years_playing: internalProfile?.years_playing,
        achievements: internalProfile?.achievements || '',
      };
    }
    return profile; // For Supabase users, use the profile directly
  }, [internalUser, internalProfile, profile]);
  
  console.log('RoleBasedProfileForm - User role:', userRole);
  console.log('RoleBasedProfileForm - Internal user:', internalUser);
  console.log('RoleBasedProfileForm - Profile:', profile);
  console.log('RoleBasedProfileForm - Loading states:', { authLoading, internalLoading, isLoading });
  
  // Show loading state
  if (isLoading) {
    return (
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-sports-green mb-4" />
          <p className="text-gray-400">Loading profile form...</p>
        </CardContent>
      </Card>
    );
  }
  
  // Check if we have user data
  if (!user) {
    return (
      <Card className="bg-red-900/20 border-red-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="h-5 w-5" />
            No User Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-300">
            Unable to load your profile data. Please try refreshing the page or contact support if the issue persists.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  // Calculate profile completion using role-specific validation with merged profile data
  const completionInfo = React.useMemo(() => {
    if (!validationProfile) return { 
      isComplete: false, 
      completionPercentage: 0, 
      missingRequiredFields: [], 
      missingRecommendedFields: [], 
      roleSpecificMessage: '' 
    };
    
    return validateProfileCompletion(validationProfile, userRole);
  }, [validationProfile, userRole]);
  
  // Security check for admin access
  if (userRole === 'admin') {
    // Only show admin controls if user is actually admin
    const isActuallyAdmin = internalUser?.role === 'admin' || profile?.role === 'admin';
    
    if (!isActuallyAdmin) {
      return (
        <Card className="bg-red-900/20 border-red-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-300">
              Administrator access is restricted to verified admin accounts only.
            </p>
          </CardContent>
        </Card>
      );
    }
    
    return <AdminControlPanel />;
  }
  
  // Enhanced Profile completion status banner
  const ProfileCompletionBanner = () => {
    if (completionInfo.isComplete) {
      return (
        <Alert className="mb-6 bg-green-900/20 border-green-500/50">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-300">
            <p className="font-semibold">Profile Complete! 🎉</p>
            <p className="text-sm mt-1">You've earned 50 points for completing your profile.</p>
          </AlertDescription>
        </Alert>
      );
    }
    
    return (
      <Alert className="mb-6 bg-blue-900/20 border-blue-500/50">
        <AlertCircle className="h-4 w-4 text-blue-400" />
        <AlertDescription>
          <div className="text-blue-200 space-y-3">
            <div>
              <p className="font-semibold text-lg">Complete Your Profile to Earn 50 Points!</p>
              <p className="text-sm text-blue-300 mt-1">{completionInfo.roleSpecificMessage}</p>
            </div>
            
            {/* Progress Bar */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>{completionInfo.completionPercentage}% Complete</span>
                <span>{completionInfo.missingRequiredFields.length} fields remaining</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-sports-green transition-all duration-500"
                  style={{ width: `${completionInfo.completionPercentage}%` }}
                />
              </div>
            </div>
            
            {/* Missing Required Fields */}
            {completionInfo.missingRequiredFields.length > 0 && (
              <div className="bg-blue-950/30 p-3 rounded-lg">
                <p className="font-medium text-sm mb-2 text-blue-300">Required Fields:</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                  {completionInfo.missingRequiredFields.map((field) => (
                    <li key={field} className="flex items-center gap-2">
                      <span className="text-red-400">●</span>
                      <span>{getFieldLabel(field)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Missing Recommended Fields (if any) */}
            {completionInfo.missingRecommendedFields && completionInfo.missingRecommendedFields.length > 0 && (
              <div className="bg-blue-950/20 p-3 rounded-lg">
                <p className="font-medium text-xs mb-2 text-blue-400">Recommended Fields (Optional):</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs">
                  {completionInfo.missingRecommendedFields.map((field) => (
                    <li key={field} className="flex items-center gap-2 text-blue-300">
                      <span className="text-yellow-400">○</span>
                      <span>{getFieldLabel(field)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  };
  
  // Render the appropriate form based on user role
  const FormComponent = () => {
    switch (userRole) {
      case 'player':
        return <EnhancedPlayerProfileForm />;
      case 'coach':
        return <CoachProfileForm />;
      case 'team-owner':
        return <TeamOwnerProfileForm />;
      case 'venue':
        return <VenueProfileForm />;
      case 'creative':
        return <CreativeProfileForm />;
      case 'support':
        return <SupportProfileForm />;
      case 'fan':
        return <FanProfileForm />;
      case 'parent':
        return <ParentProfileForm />;
      default:
        return <EnhancedPlayerProfileForm />; // Default to enhanced player form
    }
  };
  
  return (
    <>
      <ProfileCompletionBanner />
      <FormComponent />
    </>
  );
};

export default RoleBasedProfileForm;
